/**
 * Las cargas con consentimiento del oficial (la pestaña «Con consentimiento» de Público): los Excel y CSV que se
 * subieron, con su estado.
 *
 * Un borrador se retoma en el asistente (`cargas/nueva?carga=`); todo lo demás va al detalle. Con alguna
 * carga importando o borrando, la lista se refresca cada 3 s. Si el pedido falla se dice, nunca una lista
 * vacía que parezca «no hay cargas».
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileSpreadsheet, Upload } from "lucide-react";
import type { CargaPublica } from "../../types.base";
import { baseApi } from "../../baseApi";
import { usePolling } from "../../hooks/usePolling";
import { contactosDeCarga } from "../../logic/base";
import { nombreDeQuienPara } from "../../logic/formato";
import { fecha, num } from "../../../campana-nueva/utils/format";
import { CajaError, ChipEstadoCarga, Esqueleto, R_CARGAS, llamar, textoFallo } from "../../components/base/comun";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

function rutaCarga(c: Pick<CargaPublica, "id" | "estado">): string {
  const id = encodeURIComponent(c.id);
  return c.estado === "borrador" ? `${R_CARGAS}/nueva?carga=${id}` : `${R_CARGAS}/${id}`;
}

/** «Contactos» de la tabla (`contactosDeCarga`): una fallida muestra los que ya escribió. */
function contactosDe(c: CargaPublica): string {
  const n = contactosDeCarga(c);
  return n === null ? "—" : num(n);
}

export default function CargasPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CargaPublica[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  /** `lanzar`: en el refresco periódico un fallo se lanza para que `usePolling` lo cuente. */
  const cargar = useCallback(async (lanzar = false) => {
    const l = await llamar<CargaPublica[]>(() => baseApi.cargas());
    setCargando(false);
    if (!l.ok) {
      setError(textoFallo(l));
      if (lanzar) throw new Error(l.error || "sin respuesta");
      return;
    }
    setItems(Array.isArray(l.data) ? l.data : []);
    setError("");
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  const equipo = useEsEquipoDeenex();
  const enCurso = (items || []).some((c) => c.estado === "importando" || c.estado === "eliminando");
  const poll = usePolling(() => cargar(true), enCurso ? 3000 : null, []);

  // Cargar contactos es del equipo de Deenex: una persona de un cliente ve las cargas.
  const boton = equipo ? (
    <Link to={`${R_CARGAS}/nueva`} className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2">
      <Upload size={16} /> Cargar contactos con consentimiento
    </Link>
  ) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Contactos con consentimiento</h3>
          <p className="text-sm text-slate-500">Para WhatsApp oficial: cada persona queda con su sí, de dónde lo dio y cuándo.</p>
        </div>
        {boton}
      </div>

      {poll.detenido && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span>Dejamos de actualizar porque el servidor no responde.</span>
          <button type="button" onClick={poll.reintentar} className="font-semibold underline">Reintentar</button>
        </div>
      )}

      {cargando && !items ? (
        <Esqueleto filas={4} alto="h-12" />
      ) : error && !items ? (
        <CajaError titulo="No pudimos traer las cargas." detalle={error} onReintentar={() => { setCargando(true); void cargar(); }} />
      ) : !items?.length ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <FileSpreadsheet size={22} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
            Todavía no cargaste contactos. Subí un Excel o un CSV con los teléfonos y, si lo tenés, el consentimiento de cada persona.
          </p>
          <div className="mt-4">{boton}</div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
          {error && <p className="px-4 pt-3 text-xs text-amber-700">{error} Se muestra lo último que trajimos.</p>}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-4 py-2.5">Estado</th>
                <th className="px-4 py-2.5 text-right">Filas</th>
                <th className="px-4 py-2.5 text-right">Contactos</th>
                <th className="px-4 py-2.5 text-right">Con consentimiento</th>
                <th className="px-4 py-2.5">Fecha</th>
                <th className="px-4 py-2.5">Por</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((c) => (
                <tr key={c.id} onClick={() => navigate(rutaCarga(c))} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={rutaCarga(c)} onClick={(e) => e.stopPropagation()} className="font-semibold text-slate-900 hover:underline">{c.nombre}</Link>
                    <p className="text-[11px] text-slate-400 truncate max-w-[16rem]">{c.nombreArchivo}</p>
                  </td>
                  <td className="px-4 py-3"><ChipEstadoCarga estado={c.estado} progreso={c.progreso} /></td>
                  <td className="px-4 py-3 text-right tabular-nums">{num(c.filas)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{contactosDe(c)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{c.resultado ? num(c.resultado.conConsentimiento) : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fecha(c.importadaAt || c.createdAt)}</td>
                  {/* Quién la subió dicho como persona (`nombreDeQuien`), no el email del token: «hatsu-demo@operador.deenex».
                      A una persona de un cliente, alguien del equipo es «Equipo Deenex», sin su mail. */}
                  <td className="px-4 py-3 text-slate-500 truncate max-w-[12rem]">{nombreDeQuienPara(c.creadoPor?.email, equipo) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
